import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { AlertTriangle, Flag, ThumbsUp, ThumbsDown } from "lucide-react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import type { Review } from "@/types/review";

interface QuestionableReviewProps {
  reviews: (Review & { reported: boolean })[];
  checkReviewForBannedWords: (text: string) => string[];
  onReviewsChange: () => void;
}

const QuestionableReviewsList = ({ 
  reviews, 
  checkReviewForBannedWords,
  onReviewsChange
}: QuestionableReviewProps) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full border rounded-md p-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h2 className="text-xl font-bold">
            Questionable Reviews
            <span className="ml-2 text-muted-foreground">
              ({reviews.length})
            </span>
          </h2>
        </div>
        <CollapsibleTrigger asChild>
          <Button variant="ghost">
            {isOpen ? "Hide" : "Show"}
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="mt-4">
        {reviews.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground">No questionable reviews found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Airport</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Votes</TableHead>
                  <TableHead>Flagged Words</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => {
                  const bannedWords = checkReviewForBannedWords(review.review_text);
                  
                  return (
                    <TableRow key={review.id}>
                      <TableCell>
                        {review.reported && (
                          <div className="flex items-center gap-1">
                            <Flag className="h-4 w-4 text-red-500" />
                            <span className="text-xs">Reported</span>
                          </div>
                        )}
                        {bannedWords.length > 0 && (
                          <div className="flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <span className="text-xs">Banned words</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {review.profiles?.username || "Unknown user"}
                      </TableCell>
                      <TableCell>
                        <div className="truncate max-w-[150px]">
                          {review.airport_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="truncate max-w-[200px]">
                          {review.review_text}
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(review.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center">
                            <ThumbsUp className="h-3.5 w-3.5 text-green-500 mr-1" />
                            <span className="text-sm">{review.upvotes || 0}</span>
                          </div>
                          <div className="flex items-center">
                            <ThumbsDown className="h-3.5 w-3.5 text-red-500 mr-1" />
                            <span className="text-sm">{review.downvotes || 0}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {bannedWords.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {bannedWords.map((word, idx) => (
                              <span key={idx} className="px-1 py-0.5 bg-red-100 text-red-800 rounded text-xs">
                                {word}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" asChild variant="outline">
                            <Link to={`/admin/review/${review.id}`} state={{ from: '/admin/reviews' }}>
                              View
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default QuestionableReviewsList;
